/**
 * Customer Mode — Phase H Sprint 1 H3 customer-facing shell.
 *
 * Operator-mode (ClientDashboard.tsx) follows dashboard-standards.md V2
 * with Overview / Roadmap / Departments tabs.
 *
 * Customer-mode (this file) follows Phase H Sprint 1 spec:
 *   3 tab structure: Pano + Agency Hizmetleri + AI Asistan
 *   Türkçe-first
 *   Cobrand engine (accent color from tenant.feature_flags or default)
 *
 * Route: /customer/:slug (mounted in App.tsx). In Phase E1, subdomain
 * routing {slug}.growtify.app will resolve to this same shell with slug
 * extracted from hostname.
 *
 * For Phase H Sprint 1 launch this becomes the {slug}.growtify.app surface
 * for self-serve customers. Existing 18 managed clients cut over to this
 * shell during Phase E1 (Jul 14 → Aug 17).
 */

import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { detectTenantSlugFromHostname } from "../App";

// ─── Types ──────────────────────────────────────────────────────────────

interface Tenant {
  id: string;
  slug: string;
  type: string;
  vertical: string;
  service_tier: string;
  status: string;
  mrr_usd: number;
  custom_domain: string | null;
  feature_flags?: Record<string, any>;
  display_name?: string;
}

interface WorkItem {
  id: string;
  title: string;
  status: string;
  priority: string;
  owner_department: string | null;
}

interface Approval {
  id: string;
  title: string;
  content_type: string;
  status: string;
  tier: number;
  due_date?: string;
}

interface Brief {
  id: string;
  brief_type: string;
  title: string;
  cadence_period: string;
  health_indicator: string | null;
  health_score: number | null;
  created_at: string;
}

interface CrossDept {
  id: string;
  title: string;
  requester_department: string;
  assignee_department: string;
  status: string;
  priority: string;
}

// ─── Cobrand ────────────────────────────────────────────────────────────

const DEFAULT_ACCENT = "#a8e6cf";  // growtify-500
const DEFAULT_DARK = "#5fc7a3";

interface Brand {
  accent: string;
  accentDark: string;
  displayName: string;
  logoUrl?: string;
}

function brandFrom(tenant: Tenant | null): Brand {
  const ff = (tenant?.feature_flags ?? {}) as any;
  const brandConfig = ff.brand_config ?? {};
  return {
    accent: brandConfig.accent_color ?? DEFAULT_ACCENT,
    accentDark: brandConfig.accent_color_dark ?? brandConfig.accent_color ?? DEFAULT_DARK,
    displayName: tenant?.display_name ?? brandConfig.display_name ?? tenant?.slug ?? "?",
    logoUrl: brandConfig.logo_url,
  };
}

// ─── Component ──────────────────────────────────────────────────────────

type TabId = "pano" | "agency" | "ai-asistan";

const TABS: Array<{ id: TabId; label: string; icon: string }> = [
  { id: "pano", label: "Pano", icon: "📊" },
  { id: "agency", label: "Agency Hizmetleri", icon: "🛠" },
  { id: "ai-asistan", label: "AI Asistan", icon: "🤖" },
];

export default function CustomerMode() {
  // Slug comes from URL param (operator path /customer/:slug) OR
  // from hostname when on a tenant subdomain (e.g. fllat.growtify.app/)
  const params = useParams<{ slug: string }>();
  const slug = params.slug ?? detectTenantSlugFromHostname() ?? undefined;
  const [searchParams] = useSearchParams();
  const initialTab = (searchParams.get("tab") ?? "pano") as TabId;
  const [tab, setTab] = useState<TabId>(TABS.some((t) => t.id === initialTab) ? initialTab : "pano");

  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [workItems, setWorkItems] = useState<WorkItem[]>([]);
  const [approvals, setApprovals] = useState<Approval[]>([]);
  const [briefs, setBriefs] = useState<Brief[]>([]);
  const [crossDept, setCrossDept] = useState<CrossDept[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [liveTick, setLiveTick] = useState(0);
  const [realtimeStatus, setRealtimeStatus] = useState<"connecting" | "connected" | "disconnected">("disconnected");

  // Initial fetch + on liveTick (SSE event invalidates cache)
  useEffect(() => {
    if (!slug) return;
    if (liveTick === 0) setLoading(true);
    setError(null);
    Promise.all([
      api<Tenant>(`/tenants/${slug}`),
      api<{ items: WorkItem[] }>(`/work-items?tenant_id=${slug}&limit=20`),
      api<{ approvals: Approval[] }>(`/approvals?tenant_id=${slug}`),
      api<{ briefs: Brief[] }>(`/exec-briefs/list?limit=10`),
      api<{ requests: CrossDept[] }>(`/cross-dept?tenant_id=${slug}&limit=20`),
    ])
      .then(([t, wi, ap, br, cd]) => {
        setTenant(t);
        setWorkItems(wi.items ?? []);
        setApprovals(ap.approvals ?? []);
        setBriefs(br.briefs ?? []);
        setCrossDept(cd.requests ?? []);
        setLoading(false);
      })
      .catch((e) => {
        setError(e?.message ?? "load failed");
        setLoading(false);
      });
  }, [slug, liveTick]);

  // Phase E1 SSE realtime — subscribe to tenant channel + invalidate on event
  useEffect(() => {
    if (!slug) return;
    const apiBase = (import.meta as any).env?.VITE_API_BASE || "/api";
    const url = `${apiBase}/realtime/${slug}?channels=work_items,cross_dept,approvals,briefs,tool_actions`;
    setRealtimeStatus("connecting");
    const es = new EventSource(url);

    es.addEventListener("hello", () => {
      setRealtimeStatus("connected");
    });
    es.addEventListener("msg", () => {
      // Trigger a re-fetch by bumping liveTick
      setLiveTick((t) => t + 1);
    });
    es.onerror = () => {
      setRealtimeStatus("disconnected");
    };

    return () => {
      es.close();
      setRealtimeStatus("disconnected");
    };
  }, [slug]);

  const brand = useMemo(() => brandFrom(tenant), [tenant]);

  if (loading) {
    return <div className="text-center py-16 text-gray-500">Yükleniyor — {slug}…</div>;
  }
  if (error || !tenant) {
    return (
      <div className="text-center py-16">
        <div className="text-red-600">Tenant bulunamadı: {slug}</div>
        {error && <div className="text-xs text-gray-500 mt-2">{error}</div>}
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gray-50"
      style={{
        // CSS variables consumed by accent classes below
        ["--brand-accent" as any]: brand.accent,
        ["--brand-accent-dark" as any]: brand.accentDark,
      }}
    >
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Cobranded header */}
        <header
          className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden"
          style={{ borderTopWidth: 4, borderTopColor: brand.accent }}
        >
          <div className="px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {brand.logoUrl && (
                <img src={brand.logoUrl} alt={brand.displayName} className="h-8 w-8 object-contain" />
              )}
              <div>
                <h1 className="text-xl font-semibold text-gray-900">{brand.displayName}</h1>
                <p className="text-xs text-gray-500">
                  {tenant.vertical ?? "—"} · {tenant.service_tier} ·
                  <span className="ml-1 text-gray-700">${tenant.mrr_usd}/ay</span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <span title={`Realtime: ${realtimeStatus}`} className="flex items-center gap-1 text-gray-500">
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${
                  realtimeStatus === "connected" ? "bg-green-500 animate-pulse"
                  : realtimeStatus === "connecting" ? "bg-yellow-500"
                  : "bg-gray-300"
                }`} />
                {realtimeStatus === "connected" ? "canlı" : realtimeStatus === "connecting" ? "bağlanıyor" : "offline"}
              </span>
              <span className="text-gray-400">
                powered by <span className="text-gray-700 font-medium">Growtify</span>
              </span>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-t border-gray-100">
            {TABS.map((t) => {
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`flex-1 px-4 py-3 text-sm font-medium transition ${
                    active
                      ? "text-gray-900"
                      : "text-gray-500 hover:text-gray-800 hover:bg-gray-50"
                  }`}
                  style={active ? { borderBottom: `2px solid ${brand.accent}` } : { borderBottom: "2px solid transparent" }}
                >
                  <span className="mr-1.5">{t.icon}</span>{t.label}
                </button>
              );
            })}
          </div>
        </header>

        {/* Tab content */}
        {tab === "pano" && <PanoTab tenant={tenant} workItems={workItems} approvals={approvals} briefs={briefs} brand={brand} />}
        {tab === "agency" && <AgencyTab tenant={tenant} workItems={workItems} crossDept={crossDept} brand={brand} />}
        {tab === "ai-asistan" && <AiAsistanTab tenant={tenant} brand={brand} />}
      </div>
    </div>
  );
}

// ─── Tabs ───────────────────────────────────────────────────────────────

function PanoTab({ tenant, workItems, approvals, briefs, brand }: {
  tenant: Tenant; workItems: WorkItem[]; approvals: Approval[]; briefs: Brief[]; brand: Brand;
}) {
  const openWork = workItems.filter((w) => !["closed", "obsolete", "cancelled", "verified"].includes(w.status));
  const pendingApprovals = approvals.filter((a) => a.status === "pending_approval");
  const p0Count = openWork.filter((w) => w.priority === "P0").length;
  const p1Count = openWork.filter((w) => w.priority === "P1").length;

  return (
    <div className="space-y-6">
      <section>
        <h2 className="text-sm font-medium uppercase tracking-wide text-gray-500 mb-3">Bu Hafta</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi label="Açık iş" value={openWork.length} accent={brand.accent} />
          <Kpi label="P0 / P1" value={`${p0Count} / ${p1Count}`} accent={brand.accent} />
          <Kpi label="Onay Bekliyor" value={pendingApprovals.length} accent={brand.accent} />
          <Kpi label="Aylık MRR" value={`$${tenant.mrr_usd}`} accent={brand.accent} />
        </div>
      </section>

      <section>
        <h2 className="text-sm font-medium uppercase tracking-wide text-gray-500 mb-3">Yaklaşan Onaylar</h2>
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
          {pendingApprovals.length === 0 ? (
            <div className="p-4 text-xs text-gray-400">Bekleyen onay yok</div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {pendingApprovals.slice(0, 6).map((a) => (
                <li key={a.id} className="px-3 py-2 text-sm flex items-baseline justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-gray-900 truncate">{a.title}</div>
                    <div className="text-xs text-gray-500">{a.content_type} · Tier {a.tier}</div>
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: brand.accent, color: "#1f2937" }}>
                    pending
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section>
        <h2 className="text-sm font-medium uppercase tracking-wide text-gray-500 mb-3">Açık İş Listesi</h2>
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
          {openWork.length === 0 ? (
            <div className="p-4 text-xs text-gray-400">Tüm iş kalemleri tamamlandı 🎉</div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {openWork.slice(0, 8).map((w) => (
                <li key={w.id} className="px-3 py-2 text-sm flex items-baseline justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-gray-900 truncate">{w.title}</div>
                    <div className="text-xs text-gray-500">{w.owner_department ?? "—"} · {w.status}</div>
                  </div>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                    w.priority === "P0" ? "bg-red-100 text-red-700"
                    : w.priority === "P1" ? "bg-yellow-100 text-yellow-700"
                    : "bg-gray-100 text-gray-700"
                  }`}>
                    {w.priority}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section>
        <h2 className="text-sm font-medium uppercase tracking-wide text-gray-500 mb-3">Son Brifler (Agency Tarafından)</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {briefs.length === 0 ? (
            <div className="text-xs text-gray-400 p-4 col-span-2">Henüz brief yok</div>
          ) : (
            briefs.slice(0, 4).map((b) => {
              const dot = b.health_indicator === "green" ? "bg-green-500"
                : b.health_indicator === "yellow" ? "bg-yellow-500"
                : b.health_indicator === "red" ? "bg-red-500"
                : "bg-gray-400";
              return (
                <div key={b.id} className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                    <span className={`inline-block w-2 h-2 rounded-full ${dot}`} />
                    {b.title}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {b.cadence_period} · skor {b.health_score ?? "?"}/100
                  </div>
                </div>
              );
            })
          )}
        </div>
      </section>
    </div>
  );
}

function AgencyTab({ tenant, workItems, crossDept, brand }: {
  tenant: Tenant; workItems: WorkItem[]; crossDept: CrossDept[]; brand: Brand;
}) {
  const deptCounts = new Map<string, number>();
  for (const w of workItems) {
    if (w.owner_department) deptCounts.set(w.owner_department, (deptCounts.get(w.owner_department) ?? 0) + 1);
  }
  const sortedDepts = Array.from(deptCounts.entries()).sort((a, b) => b[1] - a[1]);

  // Service tier benefits (Phase H Sprint 1 placeholder list)
  const tierBenefits: Record<string, string[]> = {
    self_serve: ["AI Asistan erişimi", "Kendin yönet pano", "Kredi paketi opsiyonu"],
    managed: ["Tam ajans servisi", "Aylık raporlama", "Stratejik danışmanlık", "Tüm dept'ler aktif"],
    hybrid: ["Wholesale-style fiyatlandırma", "Custom pipeline"],
  };
  const benefits = tierBenefits[tenant.service_tier] ?? ["Standart paket"];

  return (
    <div className="space-y-6">
      <section>
        <h2 className="text-sm font-medium uppercase tracking-wide text-gray-500 mb-3">Servis Detayları</h2>
        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm">
            <div className="text-gray-900 font-medium">
              {tenant.service_tier === "managed" ? "Tam Ajans Servisi (Managed)" :
               tenant.service_tier === "self_serve" ? "Self-Serve Pano + AI" :
               tenant.service_tier === "hybrid" ? "Hibrit (Wholesale)" : tenant.service_tier}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              ${tenant.mrr_usd}/ay · {tenant.status}
            </div>
            <ul className="mt-3 text-sm space-y-1">
              {benefits.map((b) => (
                <li key={b} className="flex items-center gap-2">
                  <span style={{ color: brand.accent }}>✓</span>
                  <span className="text-gray-700">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-sm font-medium uppercase tracking-wide text-gray-500 mb-3">Aktif Departmanlar</h2>
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
          {sortedDepts.length === 0 ? (
            <div className="p-4 text-xs text-gray-400">Aktif iş yok — yeni kampanya bekleniyor</div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {sortedDepts.map(([dept, n]) => (
                <li key={dept} className="px-3 py-2 text-sm flex items-baseline justify-between">
                  <span className="text-gray-900 capitalize">{dept.replace(/_/g, " ")}</span>
                  <span className="text-gray-500 text-xs">{n} aktif iş</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section>
        <h2 className="text-sm font-medium uppercase tracking-wide text-gray-500 mb-3">Bölümler Arası Akış</h2>
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
          {crossDept.length === 0 ? (
            <div className="p-4 text-xs text-gray-400">Bekleyen aktarım yok</div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {crossDept.slice(0, 6).map((c) => (
                <li key={c.id} className="px-3 py-2 text-xs flex items-baseline justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-gray-900 truncate">{c.title}</div>
                    <div className="text-gray-500">
                      {c.requester_department} → {c.assignee_department}
                    </div>
                  </div>
                  <span className="text-gray-500 shrink-0">{c.status}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}

function AiAsistanTab({ tenant, brand }: { tenant: Tenant; brand: Brand }) {
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<Array<{ role: "user" | "assistant"; content: string }>>([]);
  const [sending, setSending] = useState(false);

  async function send() {
    if (!input.trim() || sending) return;
    const userMsg = { role: "user" as const, content: input };
    setMessages((curr) => [...curr, userMsg]);
    const messageText = input;
    setInput("");
    setSending(true);
    try {
      const resp = await api<any>(`/ai/chat`, {
        method: "POST",
        body: JSON.stringify({
          conversation_id: `customer-${tenant.slug}-${Date.now()}`,
          tenant_slug: tenant.slug,
          message: messageText,
          tools_enabled: false,
          max_tokens: 1024,
        }),
      });
      if (resp.ok) {
        setMessages((curr) => [...curr, { role: "assistant", content: resp.message ?? "" }]);
      } else {
        setMessages((curr) => [...curr, { role: "assistant", content: `[hata] ${resp.error ?? "?"}` }]);
      }
    } catch (e: any) {
      setMessages((curr) => [...curr, { role: "assistant", content: `[hata] ${e?.message ?? e}` }]);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="px-4 py-2 border-b border-gray-100 text-xs text-gray-600">
          AI Asistan — {tenant.slug} kapsamında soru sorabilirsin (kampanya durumu, brif özeti, vs.)
        </div>
        <div className="p-4 space-y-3 min-h-[300px] max-h-[500px] overflow-y-auto text-sm">
          {messages.length === 0 && (
            <div className="text-gray-400 text-center py-12">
              Sorularınızı yazın — kampanya durumu, performans, sıradaki adım…
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={m.role === "user" ? "text-right" : "text-left"}>
              <div className={`inline-block max-w-[85%] px-3 py-2 rounded-lg whitespace-pre-wrap ${
                m.role === "user" ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-900"
              }`}>
                {m.content}
              </div>
            </div>
          ))}
          {sending && <div className="text-gray-400 text-xs">AI yanıtlıyor…</div>}
        </div>
        <div className="border-t border-gray-100 p-3 flex gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); }
            }}
            placeholder="Mesajınız… (Cmd+Enter / Ctrl+Enter ile gönder)"
            rows={2}
            disabled={sending}
            className="flex-1 px-3 py-2 border border-gray-200 rounded text-sm resize-none focus:outline-none focus:border-gray-400"
          />
          <button
            onClick={send}
            disabled={!input.trim() || sending}
            className="px-4 py-2 text-white text-sm rounded disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: brand.accent, color: "#1f2937" }}
          >
            Gönder
          </button>
        </div>
      </div>

      <div className="text-xs text-gray-500">
        AI cevapları Growtify env governance + tenant verilerinizden çekiliyor.
        Detaylı analiz için ekibinizle iletişim kurabilirsiniz.
      </div>
    </div>
  );
}

// ─── Reusable bits ──────────────────────────────────────────────────────

function Kpi({ label, value, accent }: { label: string; value: string | number; accent: string }) {
  return (
    <div
      className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
      style={{ borderLeftWidth: 3, borderLeftColor: accent }}
    >
      <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-semibold text-gray-900 mt-1">{value}</div>
    </div>
  );
}
